import { useEffect, useRef, useState, type JSX, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  TableOfContents,
  type TocItem,
} from '../../components/ui/table-of-contents/table-of-contents.js';
import { PageTransition } from '../app/page-transition.js';
import { PostCard } from './post-card.js';
import { formatDate } from '../../lib/format-price.js';
import { usePreferencesStore } from '../../stores/preferences.js';
import type { BlogCategory, BlogMeta } from './types.js';
import { getRelated } from './manifest.js';

const CATEGORY_LABEL: Record<BlogCategory, string> = {
  'use-case': 'Use case',
  learning: 'Learning',
  comparison: 'Comparison',
  foundations: 'Foundations',
  news: 'News',
};

export function ArticleLayout({
  meta,
  children,
}: {
  meta: BlogMeta;
  children: ReactNode;
}): JSX.Element {
  const locale = usePreferencesStore((s) => s.locale);
  const articleRef = useRef<HTMLDivElement>(null);
  const [toc, setToc] = useState<TocItem[]>([]);
  const related = getRelated(meta.slug);

  // Build the right-rail TOC from the rendered `h2[id]` headings (ids come from
  // rehype-slug). A MutationObserver re-scans when the lazy MDX body mounts, so
  // the TOC also populates after Suspense resolves.
  useEffect(() => {
    const root = articleRef.current;
    if (!root) return;
    const scan = () => {
      const headings = Array.from(root.querySelectorAll<HTMLHeadingElement>('h2[id]'));
      setToc(headings.map((h) => ({ id: h.id, label: h.textContent ?? '' })));
    };
    scan();
    const observer = new MutationObserver(scan);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [meta.slug]);

  return (
    <PageTransition>
      <div className="flex w-full justify-between gap-12 px-6 pb-32 pt-12 standard:px-10">
        <div className="min-w-0 flex-1 wide:max-w-[760px]">
          <nav className="mb-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
            <Link to="/blog" className="transition-colors hover:text-secondary">
              Blog
            </Link>
            <span aria-hidden="true">/</span>
            <span className="text-secondary">{CATEGORY_LABEL[meta.category] ?? meta.category}</span>
          </nav>

          <h1 className="max-w-[20ch] font-sans text-[clamp(32px,5vw,48px)] font-light leading-[1.06] tracking-[-0.03em] text-primary">
            {meta.title}
          </h1>
          <p className="mt-4 max-w-[60ch] text-[18px] leading-[1.6] text-secondary">
            {meta.description}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] uppercase tracking-[0.08em] text-muted">
            <span>{meta.author ?? 'AgentBrush Team'}</span>
            <span aria-hidden="true">·</span>
            <span>{formatDate(meta.date, locale)}</span>
            {meta.readingTime ? (
              <>
                <span aria-hidden="true">·</span>
                <span>{meta.readingTime} min read</span>
              </>
            ) : null}
          </div>

          <hr className="my-5 border-white/[0.06]" />

          <div ref={articleRef}>
            <article className="[&>*:first-child]:mt-0">{children}</article>
          </div>

          <div className="mt-10 border-t border-white/[0.06] pt-6">
            <p className="text-[20px] font-light text-primary">
              Ship on-brand visuals from your agent.
            </p>
            <p className="mt-2 text-[15px] leading-[1.5] text-secondary">
              Install AgentBrush and generate your first image in minutes.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to="/getting-started"
                className="inline-flex items-center gap-2 rounded-md bg-accent-lime px-4 py-2.5 text-[13px] font-semibold text-canvas transition-[filter] hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
              >
                Get started →
              </Link>
              <Link
                to="/docs"
                className="inline-flex items-center gap-2 px-1 py-2.5 text-[13px] text-secondary transition-colors hover:text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
              >
                Read the docs →
              </Link>
            </div>
          </div>

          {related.length > 0 ? (
            <section aria-labelledby="related-heading" className="mt-16">
              <h2
                id="related-heading"
                className="mb-4 font-mono text-[11px] uppercase tracking-[0.12em] text-muted"
              >
                Keep reading
              </h2>
              <div className="grid grid-cols-1 gap-5 standard:grid-cols-3">
                {related.map((post) => (
                  <PostCard key={post.slug} post={post} dateLabel={formatDate(post.date, locale)} />
                ))}
              </div>
            </section>
          ) : null}
        </div>

        {toc.length > 0 ? <TableOfContents items={toc} /> : null}
      </div>
    </PageTransition>
  );
}
